import { useState, useEffect } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { Room, UserProfile } from '@shared/types';
import { User } from 'firebase/auth';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorBoundary } from '../components/common/ErrorBoundary';
import { RoomCard } from '@/components/public/RoomCard';
import { SEO } from '../components/common/SEO';
import { Helmet } from 'react-helmet-async';

interface PublicRoomsPageProps {
  userProfile?: UserProfile | null;
  user?: User | null; // Firebase User type
}

export function PublicRoomsPage({ userProfile, user }: PublicRoomsPageProps) {
  const [rooms, setRooms] = useState<Room[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // DIAGNOSTIC: Direct Firebase approach
  useEffect(() => {
    if (!db) {
      setError(new Error('Firebase not initialized'));
      setLoading(false);
      return;
    }

    try {
      const roomsRef = collection(db, 'rooms');

      setLoading(true);
      setError(null);

      const unsubscribe = onSnapshot(
        roomsRef,
        (snapshot) => {
          
          const roomsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          })) as Room[];

          setRooms(roomsData);
          setLoading(false);
        },
        (err) => {
          setError(err as Error);
          setLoading(false);
        }
      );

      return () => {
        unsubscribe();
      };
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      return;
    }
  }, []);



  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-600">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <SEO title="WaitlistPoker – Poker Room Manager" path="/" />
      {rooms && rooms.length > 0 && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'ItemList',
              itemListElement: rooms.map((room, idx) => ({
                '@type': 'ListItem',
                position: idx + 1,
                url: `https://waitlistpoker.com/${room.id}/tables`,
                name: room.name || 'Poker Room'
              }))
            })}
          </script>
        </Helmet>
      )}
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header userProfile={userProfile || null} user={user || null} />
        
        <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 flex-1">
          <div className="px-4 py-6 sm:px-0">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Poker Rooms</h1>
              <p className="mt-2 text-gray-600">
                Select a poker room to view available tables and join waitlists
              </p>
            </div>

            {!rooms || rooms.length === 0 ? (
              <div className="text-center py-12">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">No Rooms Available</h2>
                <p className="text-gray-600">
                  There are currently no poker rooms available. Check back later!
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {(rooms as Room[])
                  .sort((a, b) => (a.display_order || 999) - (b.display_order || 999))
                  .map((room: Room) => (
                    <RoomCard key={room.id} room={room} />
                  ))}
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </ErrorBoundary>
  );
}
